// pages/admin/ComplaintTypes.js

import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { RefreshCw } from 'lucide-react';
import ComplaintTypeModal from '../../components/admin/ComplaintTypeModal';
import {
    getComplaintTypes,
    addComplaintType,
    updateComplaintType,
    deleteComplaintType,
    refreshComplaintData
} from '../../services/complaintService';
import { clearCache } from '../../services/localStorageService';
import { getCachedData, CACHE_KEYS } from '../../services/localStorageService';

const ComplaintTypes = () => {
    const [types, setTypes] = useState([]);
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    const loadData = async () => {
        setIsLoading(true);
        try {
            // Load categories from cache or fetch
            let categoriesData = getCachedData(CACHE_KEYS.CATEGORIES);
            if (!categoriesData) {
                // If not in cache, we need to fetch them
                const { getComplaintCategories } = await import('../../services/complaintService');
                categoriesData = await getComplaintCategories();
            }
            setCategories(categoriesData);

            // Load complaint types
            const typesData = await getComplaintTypes();
            setTypes(typesData);
            setLastUpdated(new Date());
        } catch (error) {
            console.error(error);
            toast.error('Failed to load complaint types');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    // Handle refresh - clear cache and reload
    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            // Clear localStorage cache
            clearCache();

            // Refresh complaint data from Firebase
            await refreshComplaintData();

            // Reload data
            await loadData();

            toast.success('Data refreshed successfully!');
        } catch (error) {
            console.error('Refresh failed:', error);
            toast.error('Failed to refresh data');
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleSubmit = async (formData) => {
        setIsLoading(true);
        try {
            if (editingItem) {
                await updateComplaintType(editingItem.id, formData);
                toast.success('Complaint type updated');
            } else {
                await addComplaintType(formData);
                toast.success('Complaint type created');
            }
            await refreshComplaintData();
            await loadData();
            setShowModal(false);
            setEditingItem(null);
        } catch (error) {
            toast.error('Failed to save');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`Delete "${name}"?`)) return;
        setIsLoading(true);
        try {
            await deleteComplaintType(id);
            toast.success('Deleted');
            await refreshComplaintData();
            await loadData();
        } catch (error) {
            toast.error('Failed to delete');
        } finally {
            setIsLoading(false);
        }
    };

    // Get category name by ID
    const getCategoryName = (categoryId) => {
        const category = categories.find(c => c.id === categoryId);
        return category ? category.name : 'Unknown';
    };

    // Get category icon by ID
    const getCategoryIcon = (categoryId) => {
        const category = categories.find(c => c.id === categoryId);
        return category ? category.icon : '📌';
    };

    return (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
            <div className="px-6 py-5 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4 bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800">Complaint Types</h3>
                        <p className="text-sm text-gray-500">
                            {types.length} total
                            {lastUpdated && (
                                <span className="ml-2 text-xs text-gray-400">
                                    • Updated: {lastUpdated.toLocaleTimeString()}
                                </span>
                            )}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {/* Refresh Button */}
                    <button
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        className="px-4 py-2.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-all flex items-center gap-2 disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                        {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
                    </button>

                    {/* Add Type Button */}
                    <button
                        onClick={() => { setEditingItem(null); setShowModal(true); }}
                        className="px-5 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all shadow-md hover:shadow-indigo-500/25 flex items-center gap-2"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Type
                    </button>
                </div>
            </div>

            <div className="p-6">
                {isLoading ? (
                    <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full"></div></div>
                ) : types.length === 0 ? (
                    <div className="text-center py-12">
                        <span className="text-4xl">📋</span>
                        <p className="font-medium text-gray-500 mt-2">No complaint types</p>
                        <p className="text-sm text-gray-400">Click "Add Type" to create one</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {types.map((type) => {
                            const categoryName = getCategoryName(type.categoryId);
                            const categoryIcon = getCategoryIcon(type.categoryId);

                            return (
                                <div key={type.id} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 border border-gray-200 hover:shadow-lg transition hover:scale-[1.02]">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600">
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                                </svg>
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-gray-800">{type.name}</h4>
                                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                                                    <span>{categoryIcon}</span>
                                                    {categoryName}
                                                </span>
                                                <span className="ml-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                                    #{type.order || 0}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex gap-1">
                                            <button
                                                onClick={() => { setEditingItem(type); setShowModal(true); }}
                                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => handleDelete(type.id, type.name)}
                                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                    {type.description && <p className="mt-2 text-sm text-gray-600 line-clamp-2">{type.description}</p>}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <ComplaintTypeModal
                isOpen={showModal}
                onClose={() => { setShowModal(false); setEditingItem(null); }}
                onSubmit={handleSubmit}
                editingItem={editingItem}
                categories={categories}
                isLoading={isLoading}
            />
        </div>
    );
};

export default ComplaintTypes;